import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AuctionAdmin - Unified auction management hub
 * Create, monitor, and manage diamond auctions (supervised and automated)
 */
import { useState } from 'react';
import styled from 'styled-components';
import { FaChartLine, FaPlus, FaGavel, FaHistory, FaDatabase, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { TabHub } from '../../components/Hubs/TabHub';
import AuctionDashboardTab from './AuctionAdmin/AuctionDashboardTab';
import CreateAuctionTab from './AuctionAdmin/CreateAuctionTab';
import ActiveAuctionsTab from './AuctionAdmin/ActiveAuctionsTab';
import AuctionHistoryTab from './AuctionAdmin/AuctionHistoryTab';
import mockDataService from '../../services/mockDataService';
const AuctionAdmin = () => {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [isMockMode, setIsMockMode] = useState(mockDataService.isMockMode());
    const [showMockInfo, setShowMockInfo] = useState(false);
    const handleToggleMockMode = () => {
        const newMode = mockDataService.toggleMockMode();
        setIsMockMode(newMode);
        window.location.reload();
    };
    const tabs = [
        {
            id: 'dashboard',
            label: 'Dashboard',
            description: 'Overview & Stats',
            icon: _jsx(FaChartLine, {}),
            component: _jsx(AuctionDashboardTab, { onNavigateToTab: (tab) => setActiveTab(tab) }),
        },
        {
            id: 'create',
            label: 'Create Auction',
            description: 'New Diamond Auction',
            icon: _jsx(FaPlus, {}),
            component: _jsx(CreateAuctionTab, {}),
        },
        {
            id: 'active',
            label: 'Active',
            description: 'Live & Scheduled',
            icon: _jsx(FaGavel, {}),
            component: _jsx(ActiveAuctionsTab, {}),
        },
        {
            id: 'history',
            label: 'History',
            description: 'Past Auctions',
            icon: _jsx(FaHistory, {}),
            component: _jsx(AuctionHistoryTab, {}),
        },
    ];
    const mockToggle = (_jsxs(MockModeToggle, { onClick: handleToggleMockMode, active: isMockMode, children: [isMockMode ? _jsx(FaToggleOn, {}) : _jsx(FaToggleOff, {}), _jsx("span", { children: isMockMode ? 'Mock Data ON' : 'Mock Data OFF' }), _jsx(FaDatabase, {})] }));
    const infoBanner = isMockMode && showMockInfo ? (_jsxs(MockInfoBanner, { children: [_jsxs(MockInfoHeader, { children: [_jsx(MockInfoTitle, { children: "Auction Demo Mode" }), _jsx(CloseButton, { onClick: () => setShowMockInfo(false), children: "\u00D7" })] }), _jsxs(MockInfoContent, { children: [_jsxs(MockInfoSection, { children: [_jsx("strong", { children: "What's Included:" }), _jsxs("ul", { children: [_jsx("li", { children: "4 Live Auctions (2 automated, 2 supervised)" }), _jsx("li", { children: "2 Scheduled Auctions (upcoming)" }), _jsx("li", { children: "3 Closed Auctions (sold, no-sale, cancelled)" }), _jsx("li", { children: "80+ realistic bids with bid history" }), _jsx("li", { children: "Diamond snapshots with full specs" })] })] }), _jsxs(MockInfoSection, { children: [_jsx("strong", { children: "Fully Functional:" }), _jsxs("ul", { children: [_jsx("li", { children: "Place bids on live auctions" }), _jsx("li", { children: "View real-time bid history updates" }), _jsx("li", { children: "Create new auctions with wizard" }), _jsx("li", { children: "Cancel auctions with confirmation" }), _jsx("li", { children: "Supervised auction state control" }), _jsx("li", { children: "Dashboard analytics computed live" })] })] }), _jsxs(MockInfoSection, { children: [_jsx("strong", { children: "Toggle Off to return to real data" }), " - Mock changes are stored locally and won't affect production."] })] })] })) : (isMockMode ? (_jsx(MockModeBadge, { onClick: () => setShowMockInfo(!showMockInfo), children: "Demo Mode Active - Click for info" })) : null);
    return (_jsx(TabHub, { title: "Auction Management Hub", subtitle: "Create and manage diamond auctions \u2014 supervised and automated", activeTab: activeTab, tabs: tabs, onTabChange: (tabId) => setActiveTab(tabId), headerActions: mockToggle, infoBanner: infoBanner }));
};
// Styled Components
const MockModeToggle = styled.button `
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.active ? 'rgba(16, 185, 129, 0.15)' : 'var(--bg-tertiary)'};
  color: ${props => props.active ? '#10b981' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.active ? '#10b981' : 'var(--border-color)'};
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background: ${props => props.active ? 'rgba(16, 185, 129, 0.25)' : 'var(--bg-secondary)'};
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }
`;
const MockModeBadge = styled.div `
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid #10b981;
  color: #10b981;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 1rem;
  text-align: center;

  &:hover {
    background: rgba(16, 185, 129, 0.2);
  }
`;
const MockInfoBanner = styled.div `
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1));
  border: 2px solid #10b981;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;
const MockInfoHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;
const MockInfoTitle = styled.h3 `
  margin: 0;
  color: #059669;
  font-size: 1.25rem;
  font-weight: 700;
`;
const CloseButton = styled.button `
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #059669;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(16, 185, 129, 0.2);
    border-radius: 50%;
  }
`;
const MockInfoContent = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;
const MockInfoSection = styled.div `
  color: #047857;
  font-size: 0.875rem;
  line-height: 1.6;

  strong {
    display: block;
    margin-bottom: 0.5rem;
    color: #065f46;
    font-size: 1rem;
  }

  ul {
    margin: 0;
    padding-left: 1.25rem;
  }

  li {
    margin-bottom: 0.25rem;
  }
`;
export default AuctionAdmin;
